'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { QueryApi } from '@/features/query/api';
import { isEmailValid } from '@/shared/lib/validations';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Form } from '@/shared/ui/form';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { ModalProps } from '@/shared/ui/modal/types';
import { OrderMageBranding } from '@/shared/ui/ordermage-branding';
import './auth-modal.scss';

type FormValues = {
    email: string;
    password: string;
};

type Props = ModalProps & {
    children?: ReactNode;
};

type LoginModalContextValue = {
    form: UseFormReturn<FormValues>;
    onClose: ModalProps['onClose'];
};

const LoginModalContext = createContext<LoginModalContextValue | null>(null);

const useLoginModalContext = () => {
    const context = useContext(LoginModalContext);
    if (!context) {
        throw new Error('LoginModal components must be used within LoginModal.');
    }
    return context;
};

const LoginModalProvider = ({ children, ...props }: Props) => {
    const form = useForm<FormValues>({
        defaultValues: {
            email: '',
            password: '',
        },
    });

    return (
        <LoginModalContext.Provider
            value={{
                form,
                onClose: props.onClose,
            }}
        >
            {children}
        </LoginModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const LoginModalTitle = () => {
    const t = useTranslations();
    return <>{t('LOGIN_MODAL.TITLE')}</>;
};

type FormProps = ComponentProps<typeof Layout>;
const LoginModalForm = ({ children, media, ...props }: FormProps) => {
    const { form } = useLoginModalContext();
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const loginMutation = useMutation(QueryApi.Customer.mutations.Login);
    const t = useTranslations();
    const { onClose } = useLoginModalContext();

    const handleSubmit = async ({ email, password }: FormValues) => {
        const response = await loginMutation.mutateAsync({ email, password, venueId: venueQuery.data!.id });
        if (response.result === 'OK') {
            onClose();
            return;
        }
        toast(({ closeToast }) => (
            <Alert heading={t('ERRORS.INVALID_CREDENTIALS')} type="error" onClose={closeToast} />
        ));
    };

    return (
        <Layout
            element={Form}
            {...form}
            onSubmit={form.handleSubmit(handleSubmit)}
            className="auth-modal__form"
            media={media}
            {...props}
        >
            {children}
        </Layout>
    );
};

const LoginModalEmailField = () => {
    const t = useTranslations();
    return (
        <Form.TextField
            data-testid="login-modal-email-field"
            name="email"
            label="E-mail"
            type="email"
            rules={{
                required: true,
                validate: (value) => isEmailValid(value) || t('ERRORS.INVALID_EMAIL'),
            }}
        />
    );
};

const LoginModalPasswordField = () => {
    const t = useTranslations();
    return (
        <Form.PasswordField
            data-testid="login-modal-password-field"
            name="password"
            label={t('WORDS.PASSWORD')}
            rules={{
                required: true,
            }}
        />
    );
};

const LoginModalForgotPasswordButton = () => {
    const t = useTranslations();
    const handleForgotPassword = () => {
        ModalService.showPrefab('ResetPasswordModal', {});
    };
    return (
        <div className="auth-modal__forgot-password">
            <button type="button" onClick={handleForgotPassword} className="auth-modal__checkbox-btn">
                {t('LOGIN_MODAL.FORGOT_PASSWORD')}
            </button>
        </div>
    );
};

const LoginModalSubmitButton = () => {
    const t = useTranslations();
    return (
        <Button data-testid="login-modal-submit-button" className="auth-modal__button" type="submit">
            {t('LOGIN_MODAL.SUBMIT_BUTTON')}
        </Button>
    );
};

const LoginModalRegisterText = () => {
    const t = useTranslations();
    const handleShowRegister = () => {
        ModalService.showPrefab('RegisterModal', {}, { position: 'replace' });
    };
    return (
        <span className="auth-modal__register-text">
            {t.rich('LOGIN_MODAL.REGISTER', {
                button: (chunks) => (
                    <button type="button" onClick={handleShowRegister} className="auth-modal__checkbox-btn">
                        {chunks}
                    </button>
                ),
            })}
        </span>
    );
};

const LoginModalBottomPanel = () => {
    const handleShowTerms = () => {
        ModalService.showPrefab('TermsAndConditionsModal', {});
    };
    return <OrderMageBranding onLogoClick={handleShowTerms} />;
};

export const LoginModalWrapper = ({ children, ...props }: Props) => {
    return (
        <LoginModalProvider {...props}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'auth-modal',
                    containerProps: {
                        className: 'auth-modal__container',
                    },
                    'data-testid': 'login-modal',
                }}
            >
                {children}
            </Modal>
        </LoginModalProvider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const LoginModalProviderComponent = ({ children, ...props }: Props) => (
    <LoginModalProvider {...props}>{children}</LoginModalProvider>
);

export {
    LoginModalWrapper as Wrapper,
    LoginModalProviderComponent as Provider,
    LoginModalTitle as Title,
    LoginModalForm as Form,
    LoginModalEmailField as EmailField,
    LoginModalPasswordField as PasswordField,
    LoginModalForgotPasswordButton as ForgotPasswordButton,
    LoginModalSubmitButton as SubmitButton,
    LoginModalRegisterText as RegisterText,
    LoginModalBottomPanel as BottomPanel,
};

export { LoginModalWrapper as LoginModal };
